import { SubscribeConsumerEventData } from '../consumer/consumer';

export function Provides(args?: string | { context?: string; elementAttribute?: string }) {
  return (target: any, propertyKey: string): void => {
    const context = typeof args === 'string' ? args : args?.context;
    const elementAttribute = typeof args === 'string' ? 'el' : args?.elementAttribute || 'el';
    const contextKey = context ?? propertyKey;

    target.__syContextProvidesKeys = target.__syContextProvidesKeys || [];
    if (!target.__syContextProvidesKeys.find((el) => el.contextKey === contextKey)) {
      target.__syContextProvidesKeys.push({ contextKey, propertyKey });
    }

    // Define property watcher
    const watcherPropertyKey = `__syContext_${propertyKey}Watcher`;
    Object.defineProperty(target, watcherPropertyKey, {
      value() {
        if (!this.__syConsumersMap.has(contextKey)) {
          this.__syConsumersMap.set(contextKey, new Set<any>());
        }
        const consumersSet = this.__syConsumersMap.get(contextKey);
        consumersSet.forEach((el) => {
          el.changeValue(this[propertyKey]);
        });
      },
    });

    const { watchers } = target.constructor;
    Object.defineProperty(target.constructor, 'watchers', {
      get() {
        const obj = { ...(watchers ?? {}) };
        obj[propertyKey] = obj[propertyKey] ?? [];
        obj[propertyKey].push(watcherPropertyKey);
        return obj;
      },
      configurable: true,
    });

    if (target.__syContextProvidesWrapped) return;

    target.__syContextProvidesWrapped = true;

    const { connectedCallback, disconnectedCallback } = target;

    target.connectedCallback = async function () {
      this.__syConsumersMap = this.__syConsumersMap ?? new Map<string, Set<any>>();

      const connectCallbackReturn = connectedCallback?.call(this);

      const element = this[elementAttribute];
      if (!element) {
        throw `@Provides decorators requires '@Element' to be defined in the component ("${this?.constructor?.name}"). The @Element attribute name is, by default, 'el'. You can change it with a parameter in @Provides decorator.`;
      }

      this.__syContextProvidesListeningFunction = (e: CustomEvent<SubscribeConsumerEventData>) => {
        const { context } = e?.detail;
        const providerKey = target.__syContextProvidesKeys.find((key) => key.contextKey === context);

        // Not for this provider
        if (!providerKey) return;

        if (!this.__syConsumersMap.has(context)) {
          this.__syConsumersMap.set(context, new Set<any>());
        }
        const consumersSet = this.__syConsumersMap.get(context);

        if (e.detail.el && !e.detail.el.unsubscribe) {
          consumersSet.add(e.detail.el);
          e.detail.el.changeValue(this[providerKey.propertyKey]);
          e.detail.el.unsubscribe = () => {
            consumersSet.delete(e.detail.el);
            e.detail.el.unsubscribe = null;
          };
          e.stopPropagation();
        }
      };

      element?.addEventListener('syContextConsumerDidSubscribe', this.__syContextProvidesListeningFunction);

      return connectCallbackReturn;
    };

    target.disconnectedCallback = async function () {
      const disconnectedCallbackReturn = disconnectedCallback?.call(this);

      if (this.__syContextProvidesListeningFunction) {
        const element = this[elementAttribute];
        if (!element) {
          throw `@Provides decorators requires '@Element' to be defined in the component ("${this?.constructor?.name}"). The @Element attribute name is, by default, 'el'. You can change it with a parameter in @Provides decorator.`;
        }
        element?.removeEventListener('syContextConsumerDidSubscribe', this.__syContextProvidesListeningFunction);
      }
      return disconnectedCallbackReturn;
    };
  };
}
